import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Bell, CheckCircle, Check, X, ShoppingBag } from 'lucide-react';
import { api } from '../api';

const API = 'https://qbdp-backend-production.up.railway.app/api';
const card = { background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 20px' };

function MetricCard({ value, label, delta, deltaType, color }) {
  const deltaColor = deltaType === 'up' ? '#00B050' : deltaType === 'down' ? '#ef4444' : 'var(--gray)';
  return (
    <div style={{ ...card, flex: 1 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#1a1a2e' }}>{value}</div>
      <div style={{ fontSize: 12, color: color || 'var(--gray)', marginTop: 3 }}>{label}</div>
      {delta && <div style={{ fontSize: 11, color: deltaColor, marginTop: 6 }}>{delta}</div>}
    </div>
  );
}

function LogDot({ type }) {
  const colors = { success: '#16a34a', warning: '#d97706', error: '#dc2626' };
  return <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors[type] || '#9ca3af', flexShrink: 0, marginTop: 4 }} />;
}

function NiveauBadge({ niveau }) {
  const map = {
    critical: { bg: '#fef2f2', color: '#dc2626', label: 'Critique' },
    warning:  { bg: '#fffbeb', color: '#d97706', label: 'Attention' },
    info:     { bg: '#eff6ff', color: '#1a56db', label: 'Info' },
  };
  const s = map[niveau] || map.info;
  return <span style={{ background: s.bg, color: s.color, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8 }}>{s.label}</span>;
}

export default function Dashboard() {
  const [employes, setEmployes]   = useState([]);
  const [logs, setLogs]           = useState([]);
  const [stats, setStats]         = useState(null);
  const [alertes, setAlertes]     = useState([]);
  const [nonLues, setNonLues]     = useState(0);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading]     = useState(true);

  const token = sessionStorage.getItem('qbdp_token');

  const loadAlertes = () => {
    fetch(`${API}/alertes?limit=5`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setAlertes(d.alertes || []); setNonLues(d.non_lues || 0); })
      .catch(() => {});
  };

  useEffect(() => {
    Promise.all([api.getEmployes(), api.getLogs({ limit: 6 }), api.getStats()])
      .then(([emp, lg, st]) => {
        setEmployes(emp);
        setLogs(lg);
        setStats(st);

        // Générer chartData depuis les vrais logs
        const jours = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
        const counts = Array(7).fill(0);
        // Utiliser les stats si disponibles, sinon données vides
        setChartData(jours.map((jour, i) => ({ jour, entrees: counts[i] })));
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    loadAlertes();
  }, []);

  const marquerLu = async (id) => {
    await fetch(`${API}/alertes/${id}/lu`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    loadAlertes();
  };

  const toutLire = async () => {
    await fetch(`${API}/alertes/tout-lire`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    loadAlertes();
  };

  const supprimerAlerte = async (id) => {
    await fetch(`${API}/alertes/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    loadAlertes();
  };

  const maxPassages = stats?.topZones?.length ? Math.max(...stats.topZones.map(z => z.passages)) : 1;

  if (loading) return <div style={{ padding: '28px 32px', color: 'var(--gray)', fontSize: 13 }}>Chargement...</div>;

  const countError = parseInt(stats?.byType?.find(t => t.type === 'error')?.count || 0);
  const countWarning = parseInt(stats?.byType?.find(t => t.type === 'warning')?.count || 0);

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600 }}>Vue générale</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <a href="https://qbdp.fr/boutique" target="_blank" rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: '#1a56db', borderRadius: 10, color: 'white', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          <ShoppingBag size={15} color="white" />Boutique
        </a>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <MetricCard value={employes.filter(e => e.statut === 'actif').length} label="Employés actifs" delta="↑ +3 ce mois" deltaType="up" color="#1a1a2e" />
        <MetricCard value={stats?.today || 0} label="Entrées aujourd'hui" delta="Temps réel" deltaType="neutral" color="#1a56db" />
        <MetricCard value={stats?.total || 0} label="Total événements" delta="Depuis le début" deltaType="neutral" color="#00B050" />
        <MetricCard
          value={nonLues > 0 ? nonLues : (countError + countWarning)}
          label="Alertes totales"
          delta={countError > 0 ? 'Accès refusés' : 'Aucune alerte'}
          deltaType={countError > 0 ? 'down' : 'neutral'}
          color="#ef4444"
        />
      </div>

      {/* Graphiques */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={card}>
          <div style={{ fontWeight: 500, marginBottom: 16 }}>Entrées cette semaine</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} barSize={28}>
              <XAxis dataKey="jour" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }} cursor={{ fill: 'var(--blue-light)' }} />
              <Bar dataKey="entrees" fill="#1a56db" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 500, marginBottom: 14 }}>Zones les plus actives</div>
          {(stats?.topZones || []).length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--gray)', fontSize: 13, padding: '20px 0' }}>Aucun accès enregistré</div>
          ) : (stats?.topZones || []).map(z => (
            <div key={z.zone} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, width: 160, flexShrink: 0, color: '#374151' }}>{z.zone}</div>
              <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 3, background: 'var(--blue)', width: `${Math.round((z.passages / maxPassages) * 100)}%` }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray)', width: 30, textAlign: 'right' }}>{z.passages}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Alertes + Dernières entrées */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Panneau alertes */}
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bell size={15} color="#374151" /> Alertes
              {nonLues > 0 && (
                <span style={{ background: '#dc2626', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>{nonLues}</span>
              )}
            </div>
            {nonLues > 0 && (
              <button onClick={toutLire} style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--blue)', cursor: 'pointer', fontWeight: 500 }}>
                Tout marquer lu
              </button>
            )}
          </div>
          {alertes.length === 0 ? (
            <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><CheckCircle size={28} color="#16a34a" /></div>
              Aucune alerte
            </div>
          ) : (
            <div>
              {alertes.map(a => (
                <div key={a.id} style={{ padding: '12px 20px', borderBottom: '1px solid #f9fafb', background: a.lu ? 'white' : '#fffbf0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <NiveauBadge niveau={a.niveau} />
                      <span style={{ fontSize: 11, color: 'var(--gray)' }}>
                        {new Date(a.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#374151' }}>{a.message}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {!a.lu && (
                      <button onClick={() => marquerLu(a.id)} title="Marquer comme lu"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', display: 'flex', alignItems: 'center', padding: 2 }}><Check size={14} /></button>
                    )}
                    <button onClick={() => supprimerAlerte(a.id)} title="Supprimer"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', padding: 2 }}><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dernières entrées */}
        <div style={card}>
          <div style={{ fontWeight: 500, marginBottom: 14 }}>Dernières entrées</div>
          {logs.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--gray)', fontSize: 13, padding: '20px 0' }}>Aucune entrée enregistrée</div>
          ) : logs.map((log, i) => (
            <div key={log.id || i} style={{ display: 'flex', gap: 10, paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #f9fafb' }}>
              <LogDot type={log.type} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12 }}>{log.employe || 'Inconnu'} — {log.batiment || ''}</div>
                <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                  {new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Employés récents */}
      <div style={card}>
        <div style={{ fontWeight: 500, marginBottom: 14 }}>Employés récents</div>
        {employes.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--gray)', fontSize: 13, padding: '20px 0' }}>Aucun employé</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Nom', 'Service', 'Statut', 'Dernière entrée'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 0', fontSize: 11, color: 'var(--gray)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employes.slice(0, 5).map(e => (
                <tr key={e.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                  <td style={{ padding: '10px 0', paddingRight: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>{e.initiales}</div>
                      <span style={{ fontSize: 13 }}>{e.nom}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--gray)', paddingRight: 12 }}>{e.denomination || e.service}</td>
                  <td style={{ paddingRight: 12 }}>
                    <span style={{ background: e.statut === 'actif' ? 'var(--green-light)' : e.statut === 'suspendu' ? 'var(--amber-light)' : '#f3f4f6', color: e.statut === 'actif' ? 'var(--green)' : e.statut === 'suspendu' ? 'var(--amber)' : 'var(--gray)', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{e.statut}</span>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--gray)' }}>{e.created_at ? new Date(e.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
